import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
} from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { apiService } from "../services/api.service";
import { useHeaderActions } from "../context/HeaderActionsContext";
import {
  curriculumService,
  type CurriculumDetail,
  type CurriculumLayer,
  type CurriculumRemovalNode,
  type CurriculumRequestError,
  type CurriculumSummary,
  uploadCurriculumLogo,
} from "../services/curriculum.service";
import { ConfirmRemovalsDialog } from "@/components/ConfirmRemovalsDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { LessonSearchInput } from "@/components/LessonSearchInput";
import { SwapLessonButton } from "@/components/SwapLessonButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  addChildLabel,
  buildTree,
  getSlotSummary,
  isSlotComplete,
  lessonIdsInTree,
  migrateLessonsToAddedLayers,
  newTemporaryId,
  toNodeInput,
  type LessonDraft,
  type NodeDraft,
} from "./curriculum-structure";

type CurriculumSnapshot = {
  name: string;
  layers: CurriculumLayer[];
  tree: NodeStructure[];
  organizations: string[];
};

const clone = <T,>(value: T): T => structuredClone(value);

type NodeStructure = Omit<NodeDraft, "open" | "editing" | "children"> & { children: NodeStructure[] };

const withoutUiState = (nodes: NodeDraft[]): NodeStructure[] =>
  nodes.map((node) => ({
    id: node.id,
    name: node.name,
    lessons: node.lessons.map(({ id, title, lessonId }) => ({ id, title, lessonId })),
    children: withoutUiState(node.children),
  }));

const snapshotOf = (
  name: string,
  layers: CurriculumLayer[],
  tree: NodeDraft[],
  organizations: string[],
): CurriculumSnapshot => ({
  name,
  layers: clone(layers),
  tree: withoutUiState(tree),
  organizations: [...organizations].sort(),
});

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Request failed";
}

export function CurriculumAdmin({ onDirtyChange }: { onDirtyChange?: (dirty: boolean) => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeId = Number(searchParams.get("curriculumId"));
  const [curricula, setCurricula] = useState<CurriculumSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [curriculumSearch, setCurriculumSearch] = useState("");
  // Derived rather than stored, so the URL is created once per file and the
  // effect below is left with nothing to do but hand it back.
  const logoPreview = useMemo(
    () => (logoFile ? URL.createObjectURL(logoFile) : null),
    [logoFile],
  );
  useEffect(() => () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
  }, [logoPreview]);

  const onCurriculumNameLoaded = useCallback((name: string) => {
    setSearchParams((current) => {
      current.set("curriculumName", name);
      current.delete("curriculumDirty");
      return current;
    }, { replace: true });
  }, [setSearchParams]);

  const onCurriculumDirtyChange = useCallback((dirty: boolean) => {
    onDirtyChange?.(dirty);
  }, [onDirtyChange]);

  const loadCurricula = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadError(null);
      setCurricula(await curriculumService.list());
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Keyed on activeId, not mount: this component stays mounted while the editor
  // is open, so leaving the editor by the header breadcrumb, the sidebar, or the
  // browser back button re-renders the list from whatever it held before the
  // edit. Reloading whenever the list becomes visible is what keeps a freshly
  // saved organization assignment from reading "None" here.
  useEffect(() => {
    if (activeId > 0) return;
    const timer = window.setTimeout(() => void loadCurricula(), 0);
    return () => window.clearTimeout(timer);
  }, [activeId, loadCurricula]);

  const openCurriculum = (id: number, name?: string) => {
    setSearchParams((current) => {
      current.set("tab", "curriculum");
      current.set("curriculumId", String(id));
      if (name) current.set("curriculumName", name);
      return current;
    });
  };

  const createCurriculum = async () => {
    if (!newName.trim()) return;
    try {
      setIsCreating(true);
      const detail = await curriculumService.create(newName.trim());
      let savedDetail = detail;
      if (logoFile) {
        try {
          savedDetail = await uploadCurriculumLogo(detail.id, logoFile);
        } catch (logoError) {
          toast.error(`Curriculum created, but ${errorMessage(logoError).toLowerCase()}`);
        }
      }
      setNewName("");
      setLogoFile(null);
      setIsCreateOpen(false);
      setCurricula((current) => [...current, savedDetail].sort((a, b) => a.name.localeCompare(b.name)));
      openCurriculum(savedDetail.id, savedDetail.name);
      toast.success("Curriculum created");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  if (activeId > 0) {
    return (
      <CurriculumEditor
        curriculumId={activeId}
        onNameLoaded={onCurriculumNameLoaded}
        onDirtyChange={onCurriculumDirtyChange}
        onBack={() => {
          setSearchParams((current) => {
            current.delete("curriculumId");
            current.delete("curriculumName");
            current.set("tab", "curriculum");
            return current;
          });
          onDirtyChange?.(false);
        }}
      />
    );
  }

  const curriculumQuery = curriculumSearch.trim().toLowerCase();
  const filteredCurricula = curriculumQuery
    ? curricula.filter((item) => item.name.toLowerCase().includes(curriculumQuery) || String(item.id).includes(curriculumQuery))
    : curricula;
  const assignedCount = filteredCurricula.filter((item) => item.organization_count > 0).length;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <Card className="flex flex-row flex-wrap items-center justify-between gap-3 rounded-xl border-border/60 p-4 shadow-sm">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Curriculum management</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Choose a curriculum to manage its organization structure.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">{curricula.length} curricula</Badge>
          <Button onClick={() => setIsCreateOpen(true)} className="gap-2">
            <Plus className="size-4" /> Add curriculum
          </Button>
        </div>
      </Card>

      <Card className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-xl border-border/60 shadow-sm">
        <div className="flex items-center gap-2 border-b p-4">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              aria-label="Search curricula by name or ID"
              placeholder="Search curricula by name or ID"
              value={curriculumSearch}
              onChange={(event) => setCurriculumSearch(event.target.value)}
              className="pl-9"
            />
          </div>
          <Badge variant="secondary" className="shrink-0">{filteredCurricula.length} curricula</Badge>
          <Button variant="ghost" size="icon" aria-label="Refresh curricula" onClick={() => void loadCurricula()} disabled={isLoading}>
            <RefreshCw className={isLoading ? "size-4 animate-spin" : "size-4"} />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="w-full caption-bottom text-sm" aria-label="Curriculum management">
            <thead className="sticky top-0 z-10 bg-card [&_tr]:border-b">
              <tr className="border-b">
                <th className="h-10 px-4 text-left text-xs font-semibold uppercase tracking-[0.22em] text-foreground">Curriculum</th>
                <th className="h-10 w-52 px-4 text-left text-xs font-semibold uppercase tracking-[0.22em] text-foreground">Organizations</th>
                <th className="h-10 w-28 px-4 text-right text-xs font-semibold uppercase tracking-[0.22em] text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, index) => (
                  <tr key={index} className="border-b">
                    <td className="px-4 py-4"><Skeleton className="h-9 w-48" /></td>
                    <td className="px-4 py-4"><Skeleton className="h-6 w-16" /></td>
                    <td className="px-4 py-4"><Skeleton className="ml-auto h-8 w-16" /></td>
                  </tr>
                ))
              ) : loadError ? (
                <tr>
                  <td colSpan={3} className="px-4 py-12 text-center" role="alert">
                    <div className="flex flex-col items-center gap-3">
                      <AlertTriangle className="size-6 text-destructive" />
                      <p className="text-sm text-muted-foreground">{loadError}</p>
                      <Button variant="outline" onClick={() => void loadCurricula()}>Retry</Button>
                    </div>
                  </td>
                </tr>
              ) : curricula.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    No curricula configured yet.
                  </td>
                </tr>
              ) : filteredCurricula.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-12 text-center text-sm text-muted-foreground">
                    No curricula match your search.
                  </td>
                </tr>
              ) : (
                filteredCurricula.map((item) => (
                  <tr key={item.id} className="group border-b transition-colors hover:bg-muted/40">
                    <td className="px-4 py-4 align-middle">
                      <div className="flex items-center gap-3">
                        <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-lime-600/10 text-lime-900">
                          {item.logo_url ? <img src={item.logo_url} alt="" className="size-full object-cover" /> : <BookOpen className="size-5" />}
                        </span>
                        <span className="font-semibold text-foreground">{item.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-middle">
                      <span className={item.organization_count
                        ? "inline-flex items-center rounded-full border border-lime-600/20 bg-lime-600/10 px-2 py-0.5 text-xs font-medium text-lime-900"
                        : "inline-flex items-center rounded-full border border-border/60 bg-muted/30 px-2 py-0.5 text-xs font-medium text-muted-foreground"}>
                        {item.organization_count || "None"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right align-middle">
                      <Button variant="outline" size="sm" onClick={() => openCurriculum(item.id, item.name)}>Open</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t px-4 py-2.5">
          <span className="text-xs tabular-nums text-muted-foreground">
            {filteredCurricula.length ? `1-${filteredCurricula.length} of ${curricula.length}` : "0 of 0"}
          </span>
          <span className="text-xs text-muted-foreground">{assignedCount} curricula assigned</span>
        </div>
      </Card>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add curriculum</DialogTitle>
            <DialogDescription>Start with Grade and Lesson layers. Add structure after creation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2"><Label htmlFor="curriculum-name">Curriculum name</Label><Input id="curriculum-name" autoFocus value={newName} onChange={(event) => setNewName(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void createCurriculum(); }} placeholder="Example: Grade 5 Mathematics" /></div>
          <div className="space-y-2">
            <Label htmlFor="curriculum-logo">Logo <span className="font-normal text-muted-foreground">(optional)</span></Label>
            <label htmlFor="curriculum-logo" className="flex cursor-pointer items-center gap-3 rounded-lg border border-dashed border-input p-3 hover:bg-muted/30">
              <span className="flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/30">
                {logoPreview ? <img src={logoPreview} alt="" className="size-full object-cover" /> : <BookOpen className="size-5 text-muted-foreground" />}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-medium text-foreground">{logoFile ? logoFile.name : "Choose a logo"}</span>
                <span className="block text-xs text-muted-foreground">PNG, JPEG, or WebP up to 5 MB.</span>
              </span>
            </label>
            <Input id="curriculum-logo" type="file" accept="image/png,image/jpeg,image/webp" className="sr-only" onChange={(event) => setLogoFile(event.target.files?.[0] || null)} />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button><Button onClick={() => void createCurriculum()} disabled={!newName.trim() || isCreating}>{isCreating ? <Loader2 className="size-4 animate-spin" /> : null}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CurriculumEditor({ curriculumId, onNameLoaded, onDirtyChange, onBack }: { curriculumId: number; onNameLoaded: (name: string) => void; onDirtyChange: (dirty: boolean) => void; onBack: () => void }) {
  const [detail, setDetail] = useState<CurriculumDetail | null>(null);
  const [name, setName] = useState("");
  const [layers, setLayers] = useState<CurriculumLayer[]>([]);
  const [tree, setTree] = useState<NodeDraft[]>([]);
  const lessonIdsInUse = useMemo(() => lessonIdsInTree(tree), [tree]);
  const [organizations, setOrganizations] = useState<string[]>([]);
  const [availableOrganizations, setAvailableOrganizations] = useState<{ id: string; name: string }[]>([]);
  const [original, setOriginal] = useState<CurriculumSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  const [pendingRemovals, setPendingRemovals] = useState<
    CurriculumRemovalNode[] | null
  >(null);
  const { setActions } = useHeaderActions();
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [isLogoBusy, setIsLogoBusy] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // The logo endpoints are writes of their own, so they land immediately
  // rather than waiting for Save. Each bumps the curriculum's revision, and
  // the detail they return has to replace the one in hand — otherwise the
  // next structure save sends a stale revision and reads as a conflict.
  const replaceLogo = useCallback(async (file: File) => {
    try {
      setIsLogoBusy(true);
      setDetail(await uploadCurriculumLogo(curriculumId, file));
      toast.success("Curriculum logo updated");
    } catch (logoError) {
      toast.error(errorMessage(logoError));
    } finally {
      setIsLogoBusy(false);
    }
  }, [curriculumId]);

  const removeLogo = useCallback(async () => {
    try {
      setIsLogoBusy(true);
      setDetail(await curriculumService.deleteLogo(curriculumId));
      toast.success("Curriculum logo removed");
    } catch (logoError) {
      toast.error(errorMessage(logoError));
    } finally {
      setIsLogoBusy(false);
    }
  }, [curriculumId]);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [curriculum, orgs] = await Promise.all([
        curriculumService.get(curriculumId),
        apiService.getOrganizations(),
      ]);
      const nextTree = buildTree(curriculum);
      const nextLayers = curriculum.layers.length ? curriculum.layers : [{ depth: 0, name: "Grade" }, { depth: 1, name: "Lesson" }];
      const nextOrganizations = curriculum.organizations.map((organization) => organization.id);
      setDetail(curriculum);
      setName(curriculum.name);
      setLayers(nextLayers);
      setTree(nextTree);
      setOrganizations(nextOrganizations);
      setOriginal(snapshotOf(curriculum.name, nextLayers, nextTree, nextOrganizations));
      setAvailableOrganizations(orgs);
      setIsNameEditing(false);
      onNameLoaded(curriculum.name);
      setConflict(false);
    } catch (loadError) {
      setError(errorMessage(loadError));
    } finally {
      setIsLoading(false);
    }
  }, [curriculumId, onNameLoaded]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  // One organization follows one curriculum for now, so anything already bound
  // elsewhere is not an option here.
  const takenElsewhere = useMemo(
    () => detail?.organizations_taken_elsewhere ?? [],
    [detail],
  );
  const selectableOrganizations = useMemo(() => {
    const taken = new Set(takenElsewhere.map((organization) => organization.id));
    return availableOrganizations.filter(
      (organization) =>
        // Anything already selected stays listed even when it conflicts —
        // legacy data can hold both. Hiding it would leave the checkbox stuck
        // on, every save still sending it, and the API now refusing that save,
        // with nothing in the UI able to clear it.
        !taken.has(organization.id) || organizations.includes(organization.id),
    );
  }, [availableOrganizations, organizations, takenElsewhere]);

  // Only the ones actually withheld, so the note cannot claim an option is
  // missing while it is still listed for removal.
  const hiddenByConflict = useMemo(
    () =>
      takenElsewhere.filter(
        (organization) => !organizations.includes(organization.id),
      ),
    [organizations, takenElsewhere],
  );

  const dirty = useMemo(() => original !== null && JSON.stringify(snapshotOf(name, layers, tree, organizations)) !== JSON.stringify(original), [name, layers, tree, organizations, original]);
  const saved = original !== null && !dirty;
  useEffect(() => {
    onDirtyChange(dirty);
    if (!dirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty, onDirtyChange]);

  const save = useCallback(async (confirmRemovedNodeIds: number[] = []) => {
    if (!detail || !dirty) return;
    const invalidName = [...tree].some((node) => !node.name.trim());
    const invalidLesson = tree.some((node) => hasInvalidNode(node));
    if (invalidName || invalidLesson || layers.some((layer) => !layer.name.trim()) || layers.length < 2 || layers.length > 5 || layers[0]?.name.trim().toLowerCase() !== "grade" || layers.at(-1)?.name.trim().toLowerCase() !== "lesson") {
      toast.error("Complete required curriculum fields before saving");
      return;
    }
    try {
      setIsSaving(true);
      setConflict(false);
      let nextDetail = detail;
      if (name.trim() !== detail.name) {
        nextDetail = await curriculumService.rename(curriculumId, name.trim());
        setDetail(nextDetail);
        setName(nextDetail.name);
        setOriginal((current) =>
          current ? { ...current, name: nextDetail.name } : current,
        );
      }
      if (JSON.stringify(nextDetail.layers) !== JSON.stringify(layers) || JSON.stringify(withoutUiState(buildTree(nextDetail))) !== JSON.stringify(withoutUiState(tree))) {
        nextDetail = await curriculumService.updateStructure(curriculumId, { revision: nextDetail.revision, layers, nodes: toNodeInput(tree, null), confirm_removed_node_ids: confirmRemovedNodeIds });
      }
      if (JSON.stringify(nextDetail.organizations.map((item) => item.id).sort()) !== JSON.stringify([...organizations].sort())) {
        nextDetail = await curriculumService.updateOrganizations(curriculumId, organizations);
      }
      setDetail(nextDetail);
      setName(nextDetail.name);
      setLayers(nextDetail.layers);
      setTree((current) => buildTree(nextDetail, current));
      setOrganizations(nextDetail.organizations.map((item) => item.id));
      setOriginal(snapshotOf(nextDetail.name, nextDetail.layers, buildTree(nextDetail), nextDetail.organizations.map((item) => item.id)));
      setIsNameEditing(false);
      setPendingRemovals(null);
      toast.success("Curriculum changes saved");
    } catch (saveError) {
      const removals = (saveError as CurriculumRequestError).removals;
      if (removals) {
        // Not a failure yet: the save dropped published or assigned lessons and
        // the API wants them named. Show what would go and resend on confirm.
        setPendingRemovals(removals.nodes);
        return;
      }
      const message = errorMessage(saveError);
      setConflict((saveError as Error & { status?: number }).status === 409 || message.toLowerCase().includes("reload"));
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  }, [curriculumId, detail, dirty, layers, name, organizations, tree]);

  const saveRef = useRef(save);
  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    setActions(
      <div className="flex items-center gap-3">
        {saved ? <Badge variant="outline" className="rounded-full px-3 py-1 text-xs font-normal">Saved</Badge> : null}
        <Button
          className="h-9 gap-2"
          onClick={() => void saveRef.current()}
          disabled={!dirty || isSaving}
        >
          <Save className="size-4" />
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </div>,
    );
    return () => setActions(null);
  }, [dirty, isSaving, saved, setActions]);

  if (isLoading) return <div className="space-y-4" aria-live="polite" aria-busy="true"><Skeleton className="h-16 w-full" /><Skeleton className="h-72 w-full" /></div>;
  if (error || !detail) return <div className="flex flex-col items-center gap-3 p-12 text-center" role="alert"><AlertTriangle className="size-6 text-destructive" /><p className="text-sm text-muted-foreground">{error || "Curriculum not found"}</p><div className="flex gap-2"><Button variant="outline" onClick={onBack}>Back</Button><Button variant="outline" onClick={() => void load()}>Retry</Button></div></div>;

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-6xl flex-1 flex-col gap-4 overflow-auto">
      <ConfirmRemovalsDialog
        nodes={pendingRemovals}
        submitting={isSaving}
        onCancel={() => setPendingRemovals(null)}
        onConfirm={() =>
          void saveRef.current((pendingRemovals ?? []).map((node) => node.id))
        }
      />
      {!detail.can_edit ? <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950" role="status">
        <AlertTriangle className="size-4 shrink-0" />
        <span>Curriculum editing is disabled on this deployment.</span>
      </div> : null}
      {conflict ? <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm" role="alert"><span>Another editor changed this curriculum. Reload before saving.</span><Button variant="outline" size="sm" onClick={() => void load()}>Reload</Button></div> : null}

      <Card className="flex flex-row flex-wrap items-center justify-between gap-3 rounded-xl border-border/60 p-4 shadow-sm">
        <div className="min-w-0">
          <button type="button" className="mb-2 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => { if (dirty && !window.confirm("Discard unsaved curriculum changes?")) return; onBack(); }}>Back to curricula</button>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-muted/30">
              {detail.logo_url ? <img src={detail.logo_url} alt={`${name} logo`} className="size-full object-cover" /> : <BookOpen className="size-4 text-muted-foreground" />}
            </div>
            {isNameEditing ? <Input autoFocus id="curriculum-edit-name" aria-label="Curriculum name" value={name} onChange={(event) => setName(event.target.value)} disabled={!detail.can_edit} className="h-10 max-w-xl text-lg font-semibold" /> : <h2 className="text-lg font-semibold text-foreground">{name}</h2>}
            {detail.can_edit ? <Button variant="ghost" size="icon" className="size-8" aria-label="Edit curriculum name" onClick={() => setIsNameEditing(true)}><Pencil className="size-4 text-muted-foreground" /></Button> : null}
            {detail.can_edit ? <>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                aria-label="Curriculum logo file"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  // Cleared so re-picking the same file still fires a change,
                  // which is what a retry after a failed upload looks like.
                  event.target.value = "";
                  if (file) void replaceLogo(file);
                }}
              />
              <Button variant="outline" size="sm" className="h-8 gap-1.5 px-2.5 text-xs" disabled={isLogoBusy} onClick={() => logoInputRef.current?.click()}>
                {isLogoBusy ? <Loader2 className="size-3.5 animate-spin" /> : null}
                {detail.logo_url ? "Replace logo" : "Add logo"}
              </Button>
              {detail.logo_url ? <Button variant="ghost" size="sm" className="h-8 px-2.5 text-xs text-muted-foreground" disabled={isLogoBusy} onClick={() => void removeLogo()}>Remove logo</Button> : null}
            </> : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Assign organizations and configure grades, topics, and lessons.</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Badge variant="outline" className="rounded-full border-border/60 bg-muted/30 px-2.5 py-1 text-xs font-normal">{organizations.length} organizations</Badge>
          {getSlotSummary(layers, tree).map((slot) => (
            <Badge key={slot.label} variant="outline" className={isSlotComplete(slot) ? "rounded-full border-lime-600/20 bg-lime-600/10 px-2.5 py-1 text-xs font-medium text-lime-900" : "rounded-full border-border/60 bg-muted/30 px-2.5 py-1 text-xs font-normal text-muted-foreground"}>
              {slot.label}: {slot.filled}/{slot.total} filled
            </Badge>
          ))}
        </div>
      </Card>

      <Card className="rounded-xl border-border/60 p-5 shadow-sm">
        <div className="max-w-xl">
          <p className="mb-1.5 text-sm font-medium text-foreground">Assign organizations</p>
          <OrganizationMultiSelect
            selected={organizations}
            available={selectableOrganizations}
            disabled={!detail.can_edit}
            onToggle={(organizationId, checked) => setOrganizations((current) => checked ? [...new Set([...current, organizationId])] : current.filter((id) => id !== organizationId))}
          />
          <p className="mt-2 text-sm text-muted-foreground">Choose one or more organizations for this curriculum.</p>
          {hiddenByConflict.length ? (
            // Named rather than silently absent: an organization missing from
            // the list with no explanation reads as a bug.
            <p className="mt-1 text-xs text-muted-foreground">
              {hiddenByConflict.length === 1
                ? `${hiddenByConflict[0]!.name || hiddenByConflict[0]!.id} is on ${hiddenByConflict[0]!.curriculum_name} and is not available.`
                : `${hiddenByConflict.length} organizations are on other curricula and are not available.`}
            </p>
          ) : null}
        </div>
      </Card>

      <Card className="min-h-[320px] shrink-0 gap-0 rounded-xl border-border/60 p-5 shadow-sm">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
          <div><h3 className="text-sm font-semibold text-foreground">Curriculum structure</h3><p className="mt-1 text-xs text-muted-foreground">Grade is always first and Lesson is always last. Configure any layers between them.</p></div>
          <label className="flex items-center gap-2" htmlFor="layer-count"><span className="text-sm font-medium text-foreground">Layers</span><select id="layer-count" value={String(layers.length)} onChange={(event) => changeLayerCount(Number(event.target.value), layers, tree, setLayers, setTree)} disabled={!detail.can_edit} className="h-9 w-24 rounded-md border border-input bg-background px-3 text-sm outline-none focus:border-ring focus:ring-3 focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50">{[2, 3, 4, 5].map((count) => <option key={count} value={String(count)}>{count}</option>)}</select></label>
        </div>
        <div className="mb-5 flex flex-wrap items-center gap-2 rounded-lg border border-border/60 bg-muted/20 p-3">
          <span className="rounded-md bg-lime-600/10 px-2.5 py-1.5 text-xs font-semibold text-lime-900">Grade</span>
          {layers.slice(1, -1).map((layer, index) => (
            <span key={layer.depth} className="flex items-center gap-2">
              <span className="text-muted-foreground" aria-hidden="true">/</span>
              <Input aria-label={`Layer ${index + 2} name`} className="h-8 w-32 rounded-md px-2 text-xs font-medium" value={layer.name} onChange={(event) => setLayers((current) => current.map((item) => item.depth === layer.depth ? { ...item, name: event.target.value } : item))} disabled={!detail.can_edit} />
            </span>
          ))}
          <span className="text-muted-foreground" aria-hidden="true">/</span>
          <span className="rounded-md bg-lime-600/10 px-2.5 py-1.5 text-xs font-semibold text-lime-900">Lesson</span>
        </div>
        {tree.length ? <div className="mt-4 space-y-3 border-l border-border pl-3">{tree.map((node) => <NodeEditor key={node.id} node={node} level={0} layers={layers} canWrite={detail.can_edit} lessonIdsInUse={lessonIdsInUse} onChange={(next) => setTree((current) => current.map((item) => item.id === node.id ? next : item))} onRemove={() => setTree((current) => current.filter((item) => item.id !== node.id))} />)}</div> : null}
        <Button variant="outline" size="sm" className={`inline-flex h-8 w-auto shrink-0 self-start gap-1.5 border-dashed border-lime-700/40 px-2.5 text-xs font-medium text-lime-900 hover:bg-lime-600/10 ${tree.length ? "mt-4" : ""}`} onClick={() => setTree((current) => [...current, emptyNode()])} disabled={!detail.can_edit}><Plus className="size-3" />{tree.length ? "Add another grade" : "Add first grade"}</Button>
      </Card>
    </div>
  );
}

function hasInvalidNode(node: NodeDraft): boolean {
  return !node.name.trim() || node.lessons.some((lesson) => !lesson.title.trim() || (lesson.lessonId.trim() && (!Number.isInteger(Number(lesson.lessonId)) || Number(lesson.lessonId) <= 0))) || node.children.some(hasInvalidNode);
}

function OrganizationMultiSelect({
  selected,
  available,
  disabled,
  onToggle,
}: {
  selected: string[];
  available: { id: string; name: string }[];
  disabled: boolean;
  onToggle: (organizationId: string, checked: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelPosition, setPanelPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const updatePanelPosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPanelPosition({ top: rect.bottom + 8, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePanelPosition();
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [open, updatePanelPosition]);

  const visibleOrganizations = available.filter((organization) => organization.name.toLowerCase().includes(search.trim().toLowerCase()) || organization.id.toLowerCase().includes(search.trim().toLowerCase()));
  const selectedNames = selected.map((id) => available.find((organization) => organization.id === id)?.name || id);

  return (
    <div className="relative mt-3 max-w-xl">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-input bg-background px-3 text-left text-base shadow-xs outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className={selected.length ? "flex min-w-0 flex-1 flex-wrap gap-1" : "flex-1 text-muted-foreground"}>
          {selected.length ? selectedNames.slice(0, 2).map((name, index) => <span key={`${selected[index]}:${name}`} className="max-w-48 truncate rounded-md bg-muted/50 px-2 py-0.5 text-sm">{name}</span>) : "Select organizations"}
          {selectedNames.length > 2 ? <span className="rounded-md bg-muted/50 px-2 py-0.5 text-sm">+{selectedNames.length - 2}</span> : null}
        </span>
        <span className="shrink-0 text-sm text-muted-foreground">Multiple</span>
      </button>
      {open && panelPosition ? createPortal(
        <div ref={panelRef} role="listbox" aria-label="Organizations" className="fixed z-[100] rounded-lg border bg-card p-2 shadow-lg" style={{ top: panelPosition.top, left: panelPosition.left, width: panelPosition.width }}>
          <div className="relative mb-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input autoFocus aria-label="Search organizations" placeholder="Search organizations" value={search} onChange={(event) => setSearch(event.target.value)} className="h-9 pl-8" />
          </div>
          <div className="max-h-64 overflow-auto">
            {visibleOrganizations.map((organization) => (
              <label key={organization.id} className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-sm hover:bg-muted">
                <Checkbox checked={selected.includes(organization.id)} onCheckedChange={(checked) => onToggle(organization.id, checked === true)} aria-label={`Assign ${organization.name}`} />
                <span className="truncate">{organization.name}</span>
              </label>
            ))}
            {visibleOrganizations.length === 0 ? <p className="p-2 text-xs text-muted-foreground">No organizations match.</p> : null}
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}

function emptyNode(): NodeDraft { return { id: newTemporaryId(), name: "", open: true, editing: true, children: [], lessons: [] }; }
function emptyLesson(): LessonDraft { return { id: newTemporaryId(), title: "", lessonId: "", editing: true }; }

function changeLayerCount(count: number, layers: CurriculumLayer[], tree: NodeDraft[], setLayers: (value: CurriculumLayer[]) => void, setTree: (value: NodeDraft[] | ((current: NodeDraft[]) => NodeDraft[])) => void) {
  if (count < layers.length && hasChildrenAtDepth(tree, count - 2)) {
    toast.error("Remove deeper hierarchy entries before reducing layers");
    return;
  }
  const defaults = ["Unit", "Topic", "Subtopic"];
  const nextLayers = Array.from({ length: count }, (_, index) => ({
    depth: index,
    name: index === 0
      ? "Grade"
      : index === count - 1
        ? "Lesson"
        : (index < layers.length - 1 ? layers[index]?.name : undefined) || defaults[index - 1] || `Layer ${index + 1}`,
  }));
  if (count > layers.length) {
    setTree(migrateLessonsToAddedLayers(tree, layers.length, nextLayers));
  }
  setLayers(nextLayers);
}

function hasChildrenAtDepth(nodes: NodeDraft[], maxDepth: number, depth = 0): boolean {
  return nodes.some((node) => depth > maxDepth || (depth === maxDepth && node.children.length > 0) || hasChildrenAtDepth(node.children, maxDepth, depth + 1));
}

const GRADE_OPTIONS = ["Kindergarten", ...Array.from({ length: 12 }, (_, index) => `Grade ${index + 1}`)];

const NO_LESSON_IDS: ReadonlySet<string> = new Set();

export function NodeEditor({ node, level, layers, canWrite, lessonIdsInUse = NO_LESSON_IDS, onChange, onRemove }: { node: NodeDraft; level: number; layers: CurriculumLayer[]; canWrite: boolean; lessonIdsInUse?: ReadonlySet<string>; onChange: (node: NodeDraft) => void; onRemove: () => void }) {
  const isGrade = level === 0;
  const isLessonParent = level >= layers.length - 2;
  const label = layers[level]?.name || "Entry";
  const childLabel = layers[level + 1]?.name || "entry";
  const editing = node.editing ?? false;
  const childCount = isLessonParent ? node.lessons.length : node.children.length;
  const entryCount = isLessonParent
    ? `${node.lessons.length} lessons`
    : `${node.children.length} ${childLabel.toLowerCase()} entries`;
  const addLabel = addChildLabel(childLabel, node.name);
  const addChild = () => onChange(isLessonParent
    ? { ...node, open: true, lessons: [...node.lessons, emptyLesson()] }
    : { ...node, open: true, children: [...node.children, emptyNode()] });
  // ponytail: an empty node carries the add control on its own row so the next layer is visible without expanding; once it has entries the control sits at the end of that list instead
  const addButton = (className: string) => <Button variant="outline" size="sm" className={className} title={addLabel} onClick={addChild} disabled={!canWrite}><Plus className="size-3" />{addLabel}</Button>;

  return (
    <div className="rounded-md border border-border/60 bg-background">
      <div className="flex min-h-14 flex-wrap items-center gap-2 p-2">
        <Button
          variant="outline"
          size="icon"
          className="size-8 shrink-0"
          aria-label={`${node.open ? "Collapse" : "Expand"} ${label}`}
          aria-expanded={node.open}
          onClick={() => onChange({ ...node, open: !node.open })}
        >
          {node.open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </Button>
        {isGrade && editing ? (
          <select aria-label="Choose grade" value={node.name} onChange={(event) => onChange({ ...node, name: event.target.value })} disabled={!canWrite} className="h-8 min-w-44 rounded-md border border-input bg-background px-2 text-sm outline-none focus:border-ring focus:ring-3 focus:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50">
            <option value="">Choose grade</option>
            {GRADE_OPTIONS.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
          </select>
        ) : editing ? (
          <Input aria-label={`${label} name`} value={node.name} onChange={(event) => onChange({ ...node, name: event.target.value })} placeholder={`${label} name`} disabled={!canWrite} className="h-9 min-w-56 flex-1" />
        ) : (
          <span className="min-w-40 flex-1 truncate text-sm font-medium text-foreground">{node.name || `Unnamed ${label.toLowerCase()}`}</span>
        )}
        <span className="text-sm text-muted-foreground">{entryCount}</span>
        {childCount ? null : addButton("h-8 gap-1.5 px-2.5 text-xs")}
        {editing ? (
          <Button size="sm" variant="outline" className="ml-auto h-9 px-3" aria-label={`Finish editing ${label}`} onClick={() => onChange({ ...node, editing: false })} disabled={!canWrite || !node.name.trim()}>
            Done
          </Button>
        ) : (
          <Button variant="ghost" size="icon" className="size-8" aria-label={`Edit ${label}`} onClick={() => onChange({ ...node, editing: true })} disabled={!canWrite}>
            <Pencil className="size-4 text-muted-foreground" />
          </Button>
        )}
        <Button variant="ghost" size="icon" className="size-8" aria-label={`Remove ${label}`} onClick={onRemove} disabled={!canWrite}>
          <Trash2 className="size-4 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>
      {node.open ? (
        <div className="border-t border-border/60 p-3">
          <div className="ml-3 space-y-2 border-l border-border pl-4">
            {isLessonParent ? <LessonEditor lessons={node.lessons} canWrite={canWrite} parentName={node.name} lessonIdsInUse={lessonIdsInUse} onChange={(lessons) => onChange({ ...node, lessons })} /> : <>
              {node.children.map((child) => <NodeEditor key={child.id} node={child} level={level + 1} layers={layers} canWrite={canWrite} lessonIdsInUse={lessonIdsInUse} onChange={(next) => onChange({ ...node, children: node.children.map((item) => item.id === child.id ? next : item) })} onRemove={() => onChange({ ...node, children: node.children.filter((item) => item.id !== child.id) })} />)}
              {childCount ? addButton("h-9 gap-2 border-dashed border-lime-700/40 text-lime-900 hover:bg-lime-600/10") : null}
            </>}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function LessonEditor({ lessons, canWrite, onChange, parentName, lessonIdsInUse = NO_LESSON_IDS }: { lessons: LessonDraft[]; canWrite: boolean; onChange: (lessons: LessonDraft[]) => void; parentName: string; lessonIdsInUse?: ReadonlySet<string> }) {
  return (
    <div className="space-y-2">
      {lessons.map((lesson) => {
        const editing = lesson.editing ?? false;
        return (
          <div key={lesson.id} className="flex flex-wrap items-center gap-2">
            <span className="w-14 text-xs font-medium text-muted-foreground">Lesson</span>
            {editing ? (
              <>
                <LessonSearchInput
                  value={lesson.title}
                  disabled={!canWrite}
                  className="h-8"
                  onTextChange={(title) => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, title } : item))}
                  onSelect={(found) => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, title: found.name, lessonId: String(found.lesson_id), isDraft: !found.is_published } : item))}
                />
                <Input aria-label="Lesson ID" inputMode="numeric" value={lesson.lessonId} onChange={(event) => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, lessonId: event.target.value.replace(/[^0-9]/g, ""), isDraft: undefined } : item))} placeholder="Enter lesson ID" disabled={!canWrite} className="h-8 w-48 shrink-0" />
              </>
            ) : (
              <>
                {lesson.isDraft ? <span className="shrink-0 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-amber-900 uppercase">Draft</span> : null}
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{lesson.title}</span>
                {lesson.lessonId ? <span className="rounded-md bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground">ID: {lesson.lessonId}</span> : null}
              </>
            )}
            {editing ? (
              <Button size="sm" variant="outline" className="ml-auto h-7 px-2.5 text-xs" aria-label={`Finish editing ${lesson.title || "lesson"}`} onClick={() => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, editing: false } : item))} disabled={!canWrite || !lesson.title.trim()}>Done</Button>
            ) : (
              <Button variant="ghost" size="icon" className="ml-auto size-8" aria-label="Edit lesson" onClick={() => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, editing: true } : item))} disabled={!canWrite}><Pencil className="size-4 text-muted-foreground" /></Button>
            )}
            {!editing && lesson.lessonId ? (
              // A swap keeps the slot and its place; only the lesson in it
              // changes (R35). It is saved with everything else on Save.
              <SwapLessonButton
                currentId={lesson.lessonId}
                currentTitle={lesson.title}
                idsInUse={lessonIdsInUse}
                disabled={!canWrite}
                onSwap={(found) => onChange(lessons.map((item) => item.id === lesson.id ? { ...item, lessonId: String(found.lesson_id), title: found.name, isDraft: !found.is_published } : item))}
              />
            ) : null}
            <Button variant="ghost" size="icon" className="size-8" aria-label="Remove lesson" onClick={() => onChange(lessons.filter((item) => item.id !== lesson.id))} disabled={!canWrite}><Trash2 className="size-4 text-muted-foreground hover:text-destructive" /></Button>
          </div>
        );
      })}
      {lessons.length ? <Button variant="outline" size="sm" className="h-8 gap-1.5 border-dashed border-lime-700/40 px-2.5 text-xs text-lime-900 hover:bg-lime-600/10" onClick={() => onChange([...lessons, emptyLesson()])} disabled={!canWrite}><Plus className="size-3" />{addChildLabel("Lesson", parentName)}</Button> : null}
    </div>
  );
}
